import React, {
  type ComponentProps,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import { type VariantProps, tv } from 'tailwind-variants';

import useDelayer from 'common/util/useDelayer';

const textareaVariants = tv({
  slots: {
    base: 'box-border outline-none resize-none w-full rounded-sm px-3 py-2 text-body',
  },
  variants: {
    isBordered: {
      true: {
        // purposely setting ring-0 to support legacy v2 styles
        // when upgrading to v3, use the unstyled variant instead
        base: 'input-border !ring-0',
      },
    },
  },
  defaultVariants: {
    isBordered: true,
  },
});

type TextareaV2Props = ComponentProps<'textarea'> &
  VariantProps<typeof textareaVariants> & {
    autoResize?: boolean;
    autoResizeMaxRows?: number;
  };

export interface TextareaV2Ref {
  getTextarea: () => HTMLTextAreaElement | null;
  getValue: () => string;
  focus: () => void;
  getSelectionRange: () => { start: number; end: number };
  setValue: (value: string, viaTextEvent?: boolean) => void;
  setCursorPosition: (position: number) => void;
  setSelectionRange: (
    start: number,
    end: number,
    direction?: 'forward' | 'backward' | 'none',
  ) => void;
  insertText: (text: string) => void;
}

const TextareaV2 = forwardRef<TextareaV2Ref, TextareaV2Props>(
  ({ className, rows = 1, autoResize, autoResizeMaxRows, isBordered, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const styles = textareaVariants({
      isBordered,
    });

    const resizeTextarea = useCallback(() => {
      const textarea = textareaRef.current;

      if (!textarea || !autoResize) {
        return;
      }

      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;

      if (autoResizeMaxRows) {
        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight =
          Number.parseInt(computedStyle.lineHeight) ||
          Number.parseInt(computedStyle.fontSize) * 1.2;
        const maxHeight = lineHeight * autoResizeMaxRows;
        const finalHeight = Math.min(scrollHeight, maxHeight);

        textarea.style.height = `${finalHeight}px`;
        textarea.style.maxHeight = `${maxHeight}px`;
        textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      } else {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.maxHeight = 'none';
        textarea.style.overflowY = 'hidden';
      }
    }, [autoResize, autoResizeMaxRows]);

    const debouncedResize = useDelayer(resizeTextarea, 10);

    useEffect(() => {
      if (!autoResize) {
        return;
      }

      window.addEventListener('resize', debouncedResize);
      return () => {
        window.removeEventListener('resize', debouncedResize);
      };
    }, [autoResize, debouncedResize]);

    useLayoutEffect(() => {
      resizeTextarea();
    }, [resizeTextarea, props.value, props.defaultValue]);

    useImperativeHandle(ref, () => ({
      getTextarea: () => textareaRef.current,
      getValue: () => textareaRef.current?.value || '',
      focus: () => textareaRef.current?.focus(),
      getSelectionRange: () => ({
        start: textareaRef.current?.selectionStart || 0,
        end: textareaRef.current?.selectionEnd || 0,
      }),
      setValue: (value: string) => {
        if (textareaRef.current) {
          textareaRef.current.value = value;
        }
      },
      setCursorPosition: (position: number) => {
        textareaRef.current?.setSelectionRange(position, position);
      },
      setSelectionRange: (
        start: number,
        end: number,
        direction: 'forward' | 'backward' | 'none' = 'none',
      ) => {
        textareaRef.current?.setSelectionRange(start, end, direction);
      },
      insertText: (text: string) => {
        if (document.execCommand('insertText', false, text)) {
          return;
        }

        const textarea = textareaRef.current;
        if (!textarea) {
          return;
        }

        const selectionStart = textarea.selectionStart;
        const selectionEnd = textarea.selectionEnd;
        const value = textarea.value;
        const before = value.substring(0, selectionStart);
        const after = value.substring(selectionEnd);

        textarea.value = before + text + after;
        textarea.setSelectionRange(selectionStart + text.length, selectionStart + text.length);
        resizeTextarea();
      },
    }));

    return (
      <textarea
        ref={textareaRef}
        className={styles.base({ className })}
        rows={rows}
        {...props}
        onChange={(e) => {
          props.onChange?.(e);
          resizeTextarea();
        }}
      />
    );
  },
);

export default TextareaV2;
